import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { Question, AnswerPayload } from 'src/app/view-models/lms.models';

@Component({
  selector: 'app-take-assessment',
  templateUrl: './take-assessment.component.html',
  styleUrls: ['./take-assessment.component.css']
})
export class TakeAssessmentComponent implements OnInit {
  assessmentKey: string = '';
  attemptId!: number;
  assessmentTitle: string = '';
  questions: Question[] = [];
  
  currentIndex: number = 0;
  canSubmit: boolean = false;
  
  isSaving: boolean = false;
  isSubmitting: boolean = false;
  apiError: string | null = null;
  isLoading: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.assessmentKey = this.route.snapshot.paramMap.get('key') || '';
    if (this.assessmentKey) {
      this.loadAssessment();
    }
  }

  loadAssessment(): void {
    this.apiService.startAssessment(this.assessmentKey).subscribe({
      next: (res) => {
        this.attemptId = res.attempt_id;
        this.assessmentTitle = res.title;
        this.questions = res.questions;
        this.canSubmit = res.can_submit;
        
        // Land on the first unanswered question
        const firstUnanswered = this.questions.findIndex(q => !q.answered);
        this.currentIndex = firstUnanswered !== -1 ? firstUnanswered : 0;
        this.isLoading = false;
      },
      error: (err) => {
        this.apiError = 'Failed to load assessment. Please try again.';
        this.isLoading = false;
      }
    });
  }

  get currentQuestion(): Question {
    return this.questions[this.currentIndex];
  }

  get wordCount(): number {
    const text = this.currentQuestion.answer_text || '';
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  }

  nextQuestion(): void {
    if (this.currentIndex < this.questions.length - 1) {
      this.currentIndex++;
      this.apiError = null;
    }
  }

  prevQuestion(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.apiError = null;
    }
  }

  saveAnswer(): void {
    this.isSaving = true;
    this.apiError = null;
    const q = this.currentQuestion;

    // Validate descriptive empty submission
    if (q.question_type === 'descriptive' && (!q.answer_text || !q.answer_text.trim())) {
      this.apiError = "An answer is required for this question.";
      this.isSaving = false;
      return;
    }

    const payload: AnswerPayload = { question_id: q.question_id };
    if (q.question_type === 'mcq' && q.selected_option) {
      payload.selected_option = q.selected_option;
    } else if (q.question_type === 'descriptive' && q.answer_text) {
      payload.answer_text = q.answer_text;
    }

    this.apiService.saveAnswer(this.attemptId, payload).subscribe({
      next: (res) => {
        q.answered = true;
        this.canSubmit = res.can_submit;
        this.isSaving = false;
        
        // Auto-advance if there are more questions
        if (this.currentIndex < this.questions.length - 1) {
          this.currentIndex++;
        }
      },
      error: (err) => {
        this.isSaving = false;
        this.apiError = err.error?.detail || "We could not evaluate that answer just now. Please try again.";
      }
    });
  }

  submitAssessment(): void {
    if (!this.canSubmit) return;
    
    this.isSubmitting = true;
    this.apiError = null;

    this.apiService.submitAssessment(this.attemptId).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        // Navigate to the results summary page
        this.router.navigate(['/techsure/results', this.attemptId]);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.apiError = err.error?.detail || "Failed to submit assessment.";
      }
    });
  }

  saveAndExit(): void {
    this.router.navigate(['/techsure/assessments']);
  }

  get canSaveCurrent(): boolean {
    const q = this.currentQuestion;
    if (!q) return false;
    
    // Require option selection for MCQ
    if (q.question_type === 'mcq') {
      return !!q.selected_option;
    } 
    // Require text input for descriptive
    else if (q.question_type === 'descriptive') {
      return !!(q.answer_text && q.answer_text.trim().length > 0);
    }
    return false;
  }
}